import React, { useEffect, useState } from 'react';
import { Modal } from 'components';
import {
  Button,
  TabButtons,
  SemanticColor,
  Flex,
  Icon,
  Title,
  Divider,
  Form,
  Spin,
  message,
} from '@bb/elements-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteQueuePreferences,
  getQueuePreferences,
  saveQueuePreferences,
} from 'api/AnsweringOrgController';
import styled from 'styled-components';
import { omit } from 'lodash';
import type { AxiosError } from 'axios';
import type { AdditionalShowingPreferences } from '@bb/domain/dist/types';
import type {
  AppointmentPreferences,
  GroupPreferences,
  QueuePreferences,
} from '../types';
import GroupPreferencesTab from './GroupPreferencesTab';
import AppointmentPreferencesTab from './AppointmentPreferencesTab';

const TABS = [
  {
    label: 'View Brokerage Queue',
    value: 'group-preferences',
  },
  {
    label: 'View Showing Type',
    value: 'appointment-preferences',
  },
];

interface QueuePreferencesModalProps {
  open: boolean;
  onClose: () => void;
}

interface QueuePreferencesModalInnerProps extends QueuePreferencesModalProps {
  onAfterClose: () => void;
}

const QueuePreferencesModalInner: React.FC<QueuePreferencesModalInnerProps> = ({
  onAfterClose,
  open,
  onClose,
}) => {
  const [currentTab, setCurrentTab] = useState('group-preferences');
  const [modifiedGroupPreferences, setModifiedGroupPreferences] =
    useState<GroupPreferences | null>(null);

  const [form] = Form.useForm<{
    appointmentPreferences: AppointmentPreferences;
    additionalShowingPreferences: AdditionalShowingPreferences;
  }>();
  const queryClient = useQueryClient();

  const {
    data: queuePreferences,
    isFetching: isFetchingQueuePreferences,
    isLoading: isLoadingQueuePreferences,
  } = useQuery({
    queryKey: ['queuePreferences'],
    queryFn: getQueuePreferences,
    onSuccess: (responseData: QueuePreferences) => {
      form.setFieldsValue({
        appointmentPreferences: { ...responseData.appointmentPreferences },
        additionalShowingPreferences: {
          ...responseData.additionalShowingPreferences,
        },
      });
      setModifiedGroupPreferences(null);
    },
    enabled: open,
  });

  const {
    mutate: saveQueuePreferencesMutation,
    isPending: isPendingSaveQueuePreferences,
  } = useMutation({
    mutationFn: saveQueuePreferences,
    onSettled: (responseData: QueuePreferences, error: AxiosError | null) => {
      if (error) {
        const messageText =
          (error?.response?.data as { message?: string })?.message ||
          error.message ||
          'Something went wrong';
        message.error(messageText);
        return;
      }
      queryClient.setQueryData(['queuePreferences'], responseData);
      setModifiedGroupPreferences(null);
      queryClient.invalidateQueries({ queryKey: ['answeringAppointments'] });
      onClose();
    },
  });

  const {
    mutate: resetQueuePreferencesMutation,
    isPending: isPendingResetQueuePreferences,
  } = useMutation({
    mutationFn: deleteQueuePreferences,
    onSettled: (responseData: QueuePreferences, error: AxiosError | null) => {
      if (error) {
        const messageText =
          (error?.response?.data as { message?: string })?.message ||
          error.message ||
          'Something went wrong';
        message.error(messageText);
        return;
      }
      queryClient.setQueryData(['queuePreferences'], responseData);
      setModifiedGroupPreferences(null);
      queryClient.invalidateQueries({ queryKey: ['answeringAppointments'] });
      onClose();
    },
  });

  const handleClose = () => {
    if (isPendingSaveQueuePreferences || isPendingResetQueuePreferences) return;
    onClose();
  };

  const handleAfterClose = () => {
    onAfterClose();
  };

  const handleReset = () => {
    if (isPendingSaveQueuePreferences || isPendingResetQueuePreferences) return;
    resetQueuePreferencesMutation();
  };

  const handleSave = () => {
    if (isPendingSaveQueuePreferences || isPendingResetQueuePreferences) return;

    saveQueuePreferencesMutation({
      ...omit(queuePreferences, 'isModified'),
      ...form.getFieldsValue(),
      groupPreferences:
        modifiedGroupPreferences ?? queuePreferences?.groupPreferences,
    });
  };

  return (
    <Modal
      data-cy="queue-preferences-modal"
      open={open}
      destroyOnClose
      afterClose={handleAfterClose}
      title={
        <Title
          dataCy="queue-preferences-modal-title"
          level={2}
          thickness="normal"
          textAlign="center"
        >
          Edit Queue Preferences
        </Title>
      }
      onOk={handleSave}
      onCancel={handleClose}
      footer={
        <Flex justify="flex-end" gap="8px">
          <StyledLoadingButton
            dataCy="reset-queue-preferences-modal-button"
            key="reset-queue-preferences-modal-button"
            type="secondary"
            danger
            size="small"
            minWidth={0}
            loading={isPendingResetQueuePreferences}
            disabled={isFetchingQueuePreferences}
            icon={<Icon icon="undoOutlined" size={16} color="danger" />}
            onClick={handleReset}
            $color={SemanticColor.danger}
          >
            Reset
          </StyledLoadingButton>
          <Button
            dataCy="close-queue-preferences-modal-button"
            key="close-queue-preferences-modal-button"
            size="small"
            minWidth={0}
            type="secondary"
            onClick={handleClose}
          >
            Close
          </Button>
          <StyledLoadingButton
            dataCy="save-queue-preferences-button"
            key="save-queue-preferences-button"
            type="primary"
            size="small"
            minWidth={0}
            loading={isPendingSaveQueuePreferences}
            disabled={isFetchingQueuePreferences}
            icon={<Icon icon="saveOutlined" size={16} color="white" />}
            onClick={handleSave}
            $color={SemanticColor.white}
          >
            Save
          </StyledLoadingButton>
        </Flex>
      }
    >
      <Flex vertical>
        <TabButtons
          dataCy="queue-preferences-modal-tabs"
          options={TABS}
          value={currentTab}
          onChange={({ target }) => setCurrentTab(target.value)}
        />
        <Divider />
        {isLoadingQueuePreferences ? (
          <Flex justify="center" align="center" style={{ height: '200px' }}>
            <Spin dataCy="queue-preferences-loading" size="large" />
          </Flex>
        ) : (
          <>
            <TabContent $visible={currentTab === 'group-preferences'}>
              <GroupPreferencesTab
                groupPreferences={
                  modifiedGroupPreferences ??
                  queuePreferences?.groupPreferences ??
                  {}
                }
                onGroupPreferencesChange={setModifiedGroupPreferences}
              />
            </TabContent>
            <TabContent $visible={currentTab === 'appointment-preferences'}>
              <AppointmentPreferencesTab form={form} />
            </TabContent>
          </>
        )}
      </Flex>
    </Modal>
  );
};

const QueuePreferencesModal: React.FC<QueuePreferencesModalProps> = ({
  open,
  onClose,
}) => {
  const [shouldRender, setShouldRender] = useState(open);

  useEffect(() => {
    if (open) setShouldRender(true);
  }, [open]);

  if (!shouldRender) return null;

  return (
    <QueuePreferencesModalInner
      open={open}
      onClose={onClose}
      onAfterClose={() => setShouldRender(false)}
    />
  );
};

export default QueuePreferencesModal;

const StyledLoadingButton = styled(Button)<{ $color: string }>`
  &&& {
    .icon,
    .anticon {
      color: ${({ $color }) => $color};
    }
  }
` as React.FC<React.ComponentProps<typeof Button> & { $color: string }>;

const TabContent = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`;
